import { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { publicDocumentApi, type DirectQuoteResponse, type PublicBusinessContactInfo } from '@/lib/api';
import { calculateSubtotal, transformLineItems } from '@/components/TemplateDesigner/utils';
import { DocumentRenderer, buildOwlOpsPayload } from '@/components/TemplateDesigner/DocumentRenderer';
import type { TemplateSettings, DocumentData, BusinessData, DocumentSignature, SignatureLine, DocumentContract } from '@/components/TemplateDesigner/types';
import { DocumentNotFound } from './DocumentNotFound';
import { CheckoutLayout } from './CheckoutLayout';
import { FullDocumentView } from './FullDocumentView';
import { ClientSignatureModal } from '@/components/Modals/ClientSignatureModal';
import { RequestChangesModal } from '@/components/Modals/RequestChangesModal';
import { Loader2 } from 'lucide-react';

export function PublicQuote() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<DirectQuoteResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFoundBusiness, setNotFoundBusiness] = useState<PublicBusinessContactInfo | null>(null);
  const [approving, setApproving] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [signingModalOpen, setSigningModalOpen] = useState(false);
  const [signing, setSigning] = useState(false);
  const [requestChangesModalOpen, setRequestChangesModalOpen] = useState(false);

  const loadQuote = async () => {
    if (!id) return;

    const response = await publicDocumentApi.getQuote(id);

    if (response.error) {
      // Try to extract business info from error response for not found page
      try {
        const errorData = JSON.parse(response.error);
        if (errorData.business) {
          setNotFoundBusiness(errorData.business);
        }
      } catch {
        // Ignore parse errors
      }
      setLoading(false);
      return;
    }

    if (response.data) {
      setData(response.data);
    }
    setLoading(false);
  };

  // Prevent search engine indexing
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  useEffect(() => {
    loadQuote();
  }, [id]);

  // Get tax total for checkout display (use backend calculation)
  const totalTax = useMemo(() => {
    if (!data) return 0;
    return parseFloat(data.quote.total_tax);
  }, [data]);

  // Get template settings from template_config (must be before early returns)
  const templateSettings = useMemo(() => {
    if (!data?.template_config?.settings) return undefined;
    return data.template_config.settings as unknown as TemplateSettings;
  }, [data]);

  // Build document data for renderer (must be before early returns)
  const documentData: DocumentData | null = useMemo(() => {
    if (!data) return null;

    const { quote, client, taxes } = data;

    // Build signatures and signature_lines from contract_instance
    const signatures: DocumentSignature[] = [];
    const signature_lines: SignatureLine[] = [];
    let contract: DocumentContract | undefined;

    const ci = quote.contract_instance;
    if (ci) {
      // Build contract object
      contract = {
        content: ci.cached_rendered_content,
        requires_business_signature: ci.business_signature_required,
        requires_client_signature: ci.counterparty_signature_required,
      };

      // Add signature lines for required signatures (defines where signatures appear)
      if (ci.business_signature_required) {
        signature_lines.push({
          role: 'business',
          label: 'Authorized Signature',
        });
      }
      if (ci.counterparty_signature_required) {
        signature_lines.push({
          role: 'client',
          label: 'Client Signature',
        });
      }

      // Add captured signatures (fills in the data for signed lines)
      if (ci.business_signed_at && ci.business_signatory) {
        signatures.push({
          role: 'business',
          label: 'Authorized Signature',
          name: ci.business_signatory,
          image: ci.business_signature_image || undefined,
          signed_at: ci.business_signed_at,
        });
      }
      if (ci.counterparty_signed_at && ci.counterparty_signatory) {
        signatures.push({
          role: 'client',
          label: 'Client Signature',
          name: ci.counterparty_signatory,
          image: ci.counterparty_signature_image || undefined,
          signed_at: ci.counterparty_signed_at,
        });
      }
    }

    // Use computed values (markup baked into line items)
    const computedSubtotal = calculateSubtotal(quote.line_items);

    return {
      documentType: 'quote' as const,
      id: quote.id,
      quote_number: quote.quote_number,
      quote_date: quote.quote_date || '',
      due_date: quote.due_date || '',
      status: quote.status,
      client_name: client.billing_name,
      client_company: client.company_name || '',
      client_address: client.address || '',
      note_for_client: quote.note_for_client || '',
      line_items: transformLineItems(quote.line_items, taxes),
      subtotal: computedSubtotal,
      total_tax: parseFloat(quote.total_tax),  // Use backend calculation (accounts for discount)
      total_markup: 0,  // Markup is baked into line item rates, don't show separately
      total_discount: parseFloat(quote.total_discount || '0'),
      total_amount: parseFloat(quote.total_amount),
      // Custom fields
      custom_field_values: quote.custom_field_values,
      // Shipping data
      ship_enabled: quote.ship_enabled,
      ship_fee: quote.ship_fee,
      ship_date: quote.ship_date,
      ship_via: quote.ship_via,
      ship_tracking_number: quote.ship_tracking_number,
      // Contract and signature data
      ...(signatures.length > 0 ? { signatures } : {}),
      ...(signature_lines.length > 0 ? { signature_lines } : {}),
      ...(contract ? { contract } : {}),
    };
  }, [data]);

  // Build business data for renderer (must be before early returns)
  const businessData: BusinessData = useMemo(() => {
    if (!data) return { name: '', address: '', phone: '', email: '', logo: '', payment_instructions: '' };
    const { business } = data;
    return {
      name: business.name,
      address: business.address || '',
      phone: business.phone || '',
      email: business.email || '',
      logo: business.logo || '',
      payment_instructions: business.payment_instructions || '',
    };
  }, [data]);

  // On-demand PDF generation callback (must be before early returns)
  const handleDownloadPdf = useCallback(async () => {
    if (!templateSettings || !documentData) {
      throw new Error('Template settings not available');
    }
    const payload = buildOwlOpsPayload(documentData, businessData, templateSettings);
    return publicDocumentApi.renderPdf({
      template_slug: templateSettings.selectedLayout,
      data: payload.data,
      settings: payload.settings,
    });
  }, [documentData, businessData, templateSettings]);

  const handleApprove = async () => {
    if (!id) return;
    setApproving(true);

    const response = await publicDocumentApi.approveQuote(id);

    if (response.error) {
      setApproving(false);
      return;
    }

    // Reload to show updated status
    window.location.reload();
  };

  const handleDecline = () => {
    setRequestChangesModalOpen(true);
  };

  const handleSubmitChangeRequest = async (reason: string) => {
    if (!id) return;
    setDeclining(true);

    const response = await publicDocumentApi.declineQuote(id, reason);

    if (response.error) {
      setDeclining(false);
      throw new Error(response.error);
    }

    // Reload to show updated status
    window.location.reload();
  };

  const handleSignClient = async (signatory_name: string, signature_image: string) => {
    if (!id) return;
    setSigning(true);

    const response = await publicDocumentApi.signQuoteClient(id, signatory_name, signature_image);

    if (response.error) {
      setSigning(false);
      return;
    }

    // Close the modal
    setSigningModalOpen(false);
    setSigning(false);

    // If auto-approved (no Stripe payment), reload to show approved state
    if (response.data?.auto_approved) {
      window.location.reload();
      return;
    }

    // Otherwise, update local state to trigger checkout view
    // The signature is now collected, so requiresClientSignature will be false on next render
    if (data && response.data) {
      setData({
        ...data,
        quote: {
          ...data.quote,
          status: response.data.quote.status,
          contract_instance: data.quote.contract_instance
            ? {
                ...data.quote.contract_instance,
                counterparty_signatory: response.data.quote.contract_instance.counterparty_signatory,
                counterparty_signature_image: response.data.quote.contract_instance.counterparty_signature_image,
                counterparty_signed_at: response.data.quote.contract_instance.counterparty_signed_at,
              }
            : null,
        },
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!data) {
    return <DocumentNotFound documentType="Quote" business={notFoundBusiness} />;
  }

  const { quote, client, business } = data;

  const depositAmount = parseFloat(quote.deposit);

  // Signature requirement detection
  const contractInstance = quote.contract_instance;
  const requiresClientSignature = !!(
    contractInstance?.counterparty_signature_required &&
    !contractInstance?.counterparty_signed_at
  );
  const signatureCollected = !!contractInstance?.counterparty_signed_at;
  const hasStripePayment = quote.stripe_card_enabled || quote.stripe_ach_enabled;

  // Show checkout layout when: deposit + Stripe payment (signature handled inside checkout)
  const showCheckout =
    quote.status === 'awaiting_response' &&
    depositAmount > 0 &&
    hasStripePayment;

  // For quotes without Stripe payment but with signature required - show FullDocumentView with sign button
  const showSignButton =
    quote.status === 'awaiting_response' &&
    requiresClientSignature &&
    !showCheckout;

  // Determine sign button text based on flow
  const getSignButtonText = () => {
    return 'Sign to Approve';
  };

  // Show approve/decline for awaiting_response without deposit payment and no signature required
  const showApproveDecline =
    quote.status === 'awaiting_response' && !showCheckout && !showSignButton;

  // Build document content using DocumentRenderer (same approach as Template Designer)
  const documentContent = templateSettings && documentData ? (
    <DocumentRenderer
      isPublic
      data={documentData}
      settings={templateSettings}
      business={businessData}
    />
  ) : (
    <div className="p-8 text-center text-gray-500">
      Unable to render document. Please try again later.
    </div>
  );

  // Checkout view for quotes with deposit and payment enabled
  if (showCheckout) {
    return (
      <>
      <CheckoutLayout
        documentType="quote"
        documentId={quote.id}
        clientId={client.id}
        business={business}
        stripeAccountId="" // Will be fetched from payment info endpoint
        documentNumber={quote.quote_number}
        documentDate={quote.quote_date}
        dueDate={quote.due_date}
        subtotal={parseFloat(quote.subtotal)}
        totalTax={totalTax}
        totalAmount={parseFloat(quote.total_amount)}
        depositAmount={depositAmount}
        cardEnabled={quote.stripe_card_enabled}
        achEnabled={quote.stripe_ach_enabled}
        availableCredit={0} // Will be fetched from payment info endpoint
        clientName={client.billing_name}
        clientCompany={client.company_name}
        clientAddress={client.address}
        requiresSignature={contractInstance?.counterparty_signature_required ?? false}
        signatureCollected={signatureCollected}
        contractContent={contractInstance?.cached_rendered_content}
        onSignComplete={handleSignClient}
        onPaymentSuccess={() => window.location.reload()}
        onDecline={handleDecline}
        declining={declining}
        fullDocumentContent={documentContent}
        pdfUrl={quote.pdf_url}
        onDownloadPdf={templateSettings && documentData ? handleDownloadPdf : undefined}
        pdfFilename={`Quote-${quote.quote_number}.pdf`}
      />
      <RequestChangesModal
        open={requestChangesModalOpen}
        onClose={() => setRequestChangesModalOpen(false)}
        onSubmit={handleSubmitChangeRequest}
        businessName={business.name}
      />
    </>
    );
  }

  // Full document view
  const statusConfig = getQuoteStatusConfig(quote.status);

  return (
    <>
      <FullDocumentView
        documentType="quote"
        business={business}
        themeColor={templateSettings?.colorTheme?.primary}
        logoUrl={templateSettings?.logo?.url}
        showCompanyName={templateSettings?.logo?.showCompanyName ?? true}
        status={quote.status}
        statusLabel={statusConfig.label}
        statusVariant={statusConfig.variant}
        showApproveDecline={showApproveDecline}
        onApprove={handleApprove}
        onDecline={handleDecline}
        approving={approving}
        declining={declining}
        showSignButton={showSignButton}
        signButtonText={getSignButtonText()}
        onSign={() => setSigningModalOpen(true)}
        signing={signing}
        pdfUrl={quote.pdf_url}
        onDownloadPdf={templateSettings && documentData ? handleDownloadPdf : undefined}
        pdfFilename={`Quote-${quote.quote_number}.pdf`}
      >
        {documentContent}
      </FullDocumentView>

      {/* Client Signature Modal */}
      {contractInstance && (
        <ClientSignatureModal
          open={signingModalOpen}
          onClose={() => setSigningModalOpen(false)}
          onComplete={handleSignClient}
          contractContent={contractInstance.cached_rendered_content}
          businessName={business.name}
          defaultName={client.billing_name}
        />
      )}

      {/* Request Changes Modal */}
      <RequestChangesModal
        open={requestChangesModalOpen}
        onClose={() => setRequestChangesModalOpen(false)}
        onSubmit={handleSubmitChangeRequest}
        businessName={business.name}
      />
    </>
  );
}

// Status config helper
function getQuoteStatusConfig(status: string): { label: string; variant: 'default' | 'success' | 'warning' } {
  switch (status) {
    case 'approved':
      return { label: 'Approved', variant: 'success' };
    case 'awaiting_response':
      return { label: 'Awaiting Response', variant: 'warning' };
    case 'changes_requested':
      return { label: 'Changes Requested', variant: 'warning' };
    default:
      return { label: status, variant: 'default' };
  }
}
